// @ts-nocheck
// CustomerProfileScreen.tsx — Full CRM profile for a single contact.
// Shows header, KPI strip, support history table, activity timeline, contact details, tags, team.

import React, { useMemo, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Linking, TextInput,
} from 'react-native';
import { Colors, FontWeight, Radii } from '../theme/tokens';
import { useResponsive } from '../theme/responsive';
import { useApp, navigate } from '../store/AppStore';
import { ScreenShell } from '../components/WebShell';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import PrimaryButton from '../components/Button_Component/Primary_Button';
import { Chip } from '../components/Chip';
import { Customer } from '../schema/types';

// ─── CrmCustomer ──────────────────────────────────────────────────────────────
interface CrmCustomer extends Customer {
  tickets: Array<{ id: string; subject: string; status: string; priority: string; channel: string; openedAgo: string }>;
  openCount: number;
  lastContact: string | null;
}

// ─── buildCustomers ───────────────────────────────────────────────────────────
function buildCustomers(tickets: any[]): CrmCustomer[] {
  const map = new Map<string, CrmCustomer>();
  tickets.forEach(t => {
    const key = t.customer.email;
    if (!map.has(key)) {
      map.set(key, { ...t.customer, tags: t.customer.tags || [], tickets: [], openCount: 0, lastContact: null });
    }
    const c = map.get(key)!;
    c.tickets.push({ id: t.id, subject: t.subject, status: t.status, priority: t.priority, channel: t.channel, openedAgo: t.openedAgo });
    if (t.status === 'open' || t.status === 'pending') c.openCount += 1;
    if (!c.lastContact) c.lastContact = t.openedAgo;
  });
  return [...map.values()];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function statusTone(s: string): 'info' | 'warn' | 'success' | 'danger' | 'default' {
  if (s === 'open')     return 'info';
  if (s === 'pending')  return 'warn';
  if (s === 'resolved') return 'success';
  if (s === 'closed')   return 'danger';
  return 'default';
}

function channelIconName(channel: string): 'Mail' | 'Server' | 'Globe' | 'AtSign' {
  const map: Record<string, 'Mail' | 'Server' | 'Globe' | 'AtSign'> = {
    email: 'Mail', api: 'Server', web: 'Globe', agent: 'AtSign',
  };
  return map[channel] ?? 'Globe';
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface CustomerProfileScreenProps {
  email: string;
}

// ─── CustomerProfileScreen ────────────────────────────────────────────────────
export function CustomerProfileScreen({ email }: CustomerProfileScreenProps) {
  const { tickets, toast, addTicket } = useApp();
  const { isMobile, pagePadX } = useResponsive();
  const customers = useMemo(() => buildCustomers(tickets), [tickets]);
  const customer = customers.find(c => c.email === email);

  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');

  // Open the user's mail client to the contact's email.
  const handleEmail = () => {
    const addr = customer?.email;
    if (!addr) { toast('No email on file.', 'info'); return; }
    const url = 'mailto:' + addr;
    if (Platform.OS === 'web') {
      (window as any).location.href = url;
    } else {
      Linking.openURL(url).catch(() => toast('Could not open mail client.', 'danger', 'x'));
    }
  };

  // Create a ticket on behalf of the contact (addTicket navigates on success).
  const submitNewTicket = () => {
    if (!customer) return;
    const subject = newSubject.trim() || `Follow-up for ${customer.name}`;
    addTicket({
      subject,
      customerId: customer._contactId || null,
      customerEmail: customer.email,
      customerPhone: customer.phone || null,
      priority: 'medium',
      channel: 'manual',
      body: newBody.trim() || undefined,
    });
    setShowNewTicket(false);
    setNewSubject('');
    setNewBody('');
  };

  if (!customer) {
    return (
      <ScreenShell crumbs={[{ label: 'Contacts', to: '/customers' }, { label: 'Not found' }]}>
        <View style={styles.notFoundWrap}>
          <Text style={styles.notFoundText}>Customer {email} not found.</Text>
          <TouchableOpacity onPress={() => navigate('/customers')}>
            <Text style={styles.notFoundLink}>← Back to Contacts</Text>
          </TouchableOpacity>
        </View>
      </ScreenShell>
    );
  }

  const phones: string[] = (customer as any).phones?.length
    ? (customer as any).phones
    : customer.phone ? [customer.phone] : [];

  const emails: string[] = (customer as any).emails?.length
    ? (customer as any).emails
    : [customer.email];

  const kpis = [
    { label: 'Tickets', value: customer.tickets.length, sub: `${customer.openCount} open` },
    { label: 'Phones',  value: phones.length,            sub: 'on record' },
    { label: 'Emails',  value: emails.length,            sub: 'on record' },
    { label: 'Tags',    value: (customer.tags || []).length, sub: 'segments' },
  ];

  const activityItems = [
    {
      iconName: 'Mail'    as const, color: Colors.info,
      text: `Replied to ticket ${customer.tickets[0]?.id ?? '—'} via email`,
      time: (customer as any).lastActive ?? customer.lastContact ?? '—',
    },
    {
      iconName: 'Ticket'  as const, color: Colors.accent,
      text: `Opened ticket ${customer.tickets[0]?.id ?? '—'}`,
      time: customer.lastContact ?? '—',
    },
    {
      iconName: 'Star'    as const, color: Colors.warn,
      text: 'Rated previous ticket 5 / 5 in CSAT',
      time: '—',
    },
    {
      iconName: 'User'    as const, color: Colors.ink3,
      text: 'Created an sk-tickets account',
      time: `Since ${(customer as any).since ?? '—'}`,
    },
  ];

  return (
    <ScreenShell crumbs={[{ label: 'Contacts', to: '/customers' }, { label: customer.name }]}>
      <ScrollView style={styles.scrollRoot} contentContainerStyle={styles.scrollContent}>

        {/* ── Premium accent strip ───────────────────────────────────────── */}
        <View style={styles.topAccent} />

        {/* ── Profile Header ─────────────────────────────────────────────── */}
        <View style={[styles.profileHeader, { paddingHorizontal: pagePadX }]}>
          <View style={[styles.profileHeaderInner, isMobile && { flexWrap: 'wrap' }]}>
            <View style={styles.avatarRing}>
              <Avatar initials={customer.initials} color={customer.color} size="lg" />
            </View>
            <View style={styles.profileHeaderInfo}>
              <Text style={styles.profileName}>{customer.name}</Text>
              <View style={styles.profileMeta}>
                <Text style={styles.profileEmail}>{customer.email}</Text>
                {customer.phone ? (
                  <>
                    <Text style={styles.metaDot}>·</Text>
                    <Text style={styles.profilePhone}>{customer.phone}</Text>
                  </>
                ) : null}
                {customer.org && customer.org !== '—' ? (
                  <>
                    <Text style={styles.metaDot}>·</Text>
                    <Text style={styles.profileOrg}>at <Text style={styles.profileOrgBold}>{customer.org}</Text></Text>
                  </>
                ) : null}
              </View>
              {customer.tags && customer.tags.length > 0 && (
                <View style={styles.tagsRow}>
                  {customer.tags.map(t => (
                    <Chip key={t} label={t} tone="accent" dot size="sm" />
                  ))}
                </View>
              )}
            </View>
            <View style={styles.profileActions}>
              <PrimaryButton
                title="Email"
                size="md"
                variant="default"
                leftIcon={<Icon name="Mail" size={13} color={Colors.ink2} />}
                onPress={handleEmail}
              />
              <PrimaryButton
                title="New ticket"
                size="md"
                variant="primary"
                leftIcon={<Icon name="Plus" size={13} color={Colors.white} />}
                onPress={() => setShowNewTicket(true)}
              />
            </View>
          </View>
        </View>

        {/* ── Body: two-column layout ────────────────────────────────────── */}
        <View style={[styles.bodyGrid, { paddingHorizontal: pagePadX }, isMobile && { flexDirection: 'column' }]}>

          {/* Left column */}
          <View style={styles.leftCol}>

            {/* KPI strip */}
            <View style={styles.kpiStrip}>
              {kpis.map(k => (
                <View key={k.label} style={styles.kpiCard}>
                  <Text style={styles.kpiLabel}>{k.label}</Text>
                  <Text style={styles.kpiValue}>{k.value}</Text>
                  <Text style={styles.kpiSub}>{k.sub}</Text>
                </View>
              ))}
            </View>

            {/* Support history */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Support history</Text>
                <Chip label={String(customer.tickets.length)} size="sm" />
                <View style={{ flex: 1 }} />
                <TouchableOpacity
                  style={styles.ghostBtn}
                  onPress={() => navigate('/tickets')}
                  activeOpacity={0.8}
                >
                  <Text style={styles.ghostBtnText}>View all tickets</Text>
                </TouchableOpacity>
              </View>

              {/* Table header */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.th, { width: 88 }]}>Ticket</Text>
                <Text style={[styles.th, { flex: 1 }]}>Subject</Text>
                <Text style={[styles.th, { width: 90 }]}>Channel</Text>
                <Text style={[styles.th, { width: 90 }]}>Status</Text>
                <Text style={[styles.th, { width: 110 }]}>Opened</Text>
              </View>

              {customer.tickets.length === 0 ? (
                <View style={styles.emptyRow}>
                  <Text style={styles.emptyRowText}>No tickets yet.</Text>
                </View>
              ) : (
                customer.tickets.map((t, idx) => (
                  <TouchableOpacity
                    key={t.id}
                    style={[
                      styles.tableRow,
                      styles.tableDataRow,
                      idx < customer.tickets.length - 1 && styles.tableRowBorder,
                    ]}
                    onPress={() => navigate('/tickets/' + t.id)}
                    activeOpacity={0.85}
                  >
                    <Text style={[styles.tdMono, { width: 88 }]}>{t.id}</Text>
                    <Text style={[styles.td, { flex: 1 }]} numberOfLines={1}>{t.subject}</Text>
                    <View style={{ width: 90, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Icon name={channelIconName(t.channel)} size={12} color={Colors.ink3} />
                      <Text style={styles.td}>{t.channel.charAt(0).toUpperCase() + t.channel.slice(1)}</Text>
                    </View>
                    <View style={{ width: 90 }}>
                      <Chip
                        label={t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                        tone={statusTone(t.status)}
                        dot
                        size="sm"
                      />
                    </View>
                    <Text style={[styles.tdSub, { width: 110 }]}>{t.openedAgo}</Text>
                  </TouchableOpacity>
                ))
              )}
            </View>

            {/* Recent activity */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Recent activity</Text>
              </View>
              <View style={styles.timeline}>
                {activityItems.map((a, i) => (
                  <View key={i} style={styles.timelineItem}>
                    <View style={[styles.timelineIcon, { backgroundColor: Colors.paper2 }]}>
                      <Icon name={a.iconName} size={13} color={a.color} />
                    </View>
                    <Text style={styles.timelineText}>{a.text}</Text>
                    <Text style={styles.timelineTime}>{a.time}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          {/* Right column */}
          <View style={styles.rightCol}>

            {/* Contact details */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Contact details</Text>
                <View style={{ flex: 1 }} />
                <Text style={styles.cardHint}>Managed from their tickets</Text>
              </View>
              <View style={styles.detailsBody}>

                {/* Phones */}
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionLabel}>Phones (E.164)</Text>
                  {phones.length > 0 ? phones.map((p, i) => (
                    <View key={i} style={styles.detailRow}>
                      <Icon name="User" size={12} color={Colors.ink4} />
                      <Text style={styles.detailMono}>{p}</Text>
                      {i === 0 && <Chip label="primary" size="sm" />}
                    </View>
                  )) : (
                    <Text style={styles.detailEmpty}>No phone on record</Text>
                  )}
                </View>

                {/* Emails */}
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionLabel}>Emails</Text>
                  {emails.map((m, i) => (
                    <View key={i} style={styles.detailRow}>
                      <Icon name="Mail" size={12} color={Colors.ink4} />
                      <Text style={[styles.detailMono, styles.detailMonoBreak]}>{m}</Text>
                      {i === 0 && <Chip label="primary" size="sm" />}
                    </View>
                  ))}
                </View>

                {/* Company */}
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionLabel}>Company</Text>
                  <View style={styles.detailRow}>
                    <Icon name="Users" size={12} color={Colors.ink4} />
                    <Text style={customer.org && customer.org !== '—' ? styles.detailText : styles.detailEmpty}>
                      {customer.org && customer.org !== '—' ? customer.org : 'None set'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Tags */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Tags</Text>
              </View>
              <View style={styles.tagsPanel}>
                {customer.tags && customer.tags.length > 0
                  ? customer.tags.map(t => <Chip key={t} label={t} outline size="sm" />)
                  : <Text style={styles.detailEmpty}>No tags yet</Text>}
                <Chip label="+ Add" outline size="sm" />
              </View>
            </View>

            {/* Account team */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Account team</Text>
              </View>
              <View style={styles.teamList}>
                {(() => {
                  const assignees = [...new Set(
                    customer.tickets
                      .filter((t: any) => t.assignee && t.assignee !== 'Unassigned')
                      .map((t: any) => t.assignee)
                  )];
                  if (assignees.length === 0) {
                    return (
                      <View style={styles.teamMember}>
                        <View style={[styles.teamMemberInfo, { paddingVertical: 4 }]}>
                          <Text style={styles.teamMemberRole}>Unassigned</Text>
                        </View>
                      </View>
                    );
                  }
                  const COLORS_LIST = [
                    'linear-gradient(135deg,#f97316,#db2777)',
                    'linear-gradient(135deg,#10b981,#0ea5e9)',
                    'linear-gradient(135deg,#a855f7,#ec4899)',
                    'linear-gradient(135deg,#6366f1,#06b6d4)',
                  ];
                  return assignees.map((name: string, idx: number) => (
                    <View key={name} style={styles.teamMember}>
                      <Avatar
                        initials={(name || 'AG').slice(0, 2).toUpperCase()}
                        color={COLORS_LIST[idx % COLORS_LIST.length]}
                        size="sm"
                      />
                      <View style={styles.teamMemberInfo}>
                        <Text style={styles.teamMemberName}>{name}</Text>
                        <Text style={styles.teamMemberRole}>Support agent</Text>
                      </View>
                    </View>
                  ));
                })()}
              </View>
            </View>

          </View>
        </View>
      </ScrollView>

      {showNewTicket && (
        <View style={mStyles.overlay}>
          <View style={mStyles.modal}>
            <View style={mStyles.head}>
              <Text style={mStyles.title}>New ticket for {customer.name}</Text>
              <TouchableOpacity onPress={() => setShowNewTicket(false)}>
                <Icon name="X" size={14} color={Colors.ink3} />
              </TouchableOpacity>
            </View>
            <View style={mStyles.body}>
              <Text style={mStyles.label}>Subject</Text>
              <TextInput
                style={mStyles.input}
                value={newSubject}
                onChangeText={setNewSubject}
                placeholder={`Follow-up for ${customer.name}`}
                autoFocus
              />
              <Text style={mStyles.label}>Description (optional)</Text>
              <TextInput
                style={[mStyles.input, mStyles.textarea]}
                value={newBody}
                onChangeText={setNewBody}
                placeholder="Add context for this ticket…"
                multiline
              />
              <Text style={mStyles.hint}>Creates a manual ticket with {customer.email} as the requester.</Text>
            </View>
            <View style={mStyles.foot}>
              <PrimaryButton title="Cancel" size="md" variant="default" onPress={() => setShowNewTicket(false)} />
              <PrimaryButton title="Create ticket" size="md" variant="primary" onPress={submitNewTicket} />
            </View>
          </View>
        </View>
      )}
    </ScreenShell>
  );
}

const mStyles = StyleSheet.create({
  overlay: {
    position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(16,24,40,0.42)',
    justifyContent: 'center', alignItems: 'center', zIndex: 100, padding: 24,
  },
  modal: { width: 480, maxWidth: '90%' as any, backgroundColor: Colors.card, borderRadius: Radii.r4, overflow: 'hidden' },
  head: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 16, borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  title: { fontSize: 16, fontWeight: FontWeight.semibold, color: Colors.ink },
  body: { padding: 18, gap: 8 },
  label: { fontSize: 12.5, fontWeight: FontWeight.medium, color: Colors.ink2, marginTop: 6 },
  input: {
    borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r2,
    paddingHorizontal: 10, paddingVertical: 8, fontSize: 13.5, color: Colors.ink, backgroundColor: Colors.card,
  },
  textarea: { minHeight: 72, textAlignVertical: 'top' as any },
  hint: { fontSize: 11.5, color: Colors.ink3, marginTop: 4 },
  foot: {
    flexDirection: 'row', justifyContent: 'flex-end', gap: 8,
    padding: 14, borderTopWidth: 1, borderTopColor: Colors.line, backgroundColor: Colors.paper2,
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  // Not found
  notFoundWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  notFoundText: { fontSize: 14, color: Colors.ink3 },
  notFoundLink: { fontSize: 13, color: Colors.accent, fontWeight: FontWeight.medium },

  scrollRoot: { flex: 1 },
  scrollContent: { paddingBottom: 40 },

  // Premium accent strip across the very top of the profile.
  topAccent: {
    height: 3,
    backgroundColor: Colors.accent,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 45%, #6366F1 100%)' } as any
      : {}),
  },

  // Profile header
  profileHeader: {
    paddingHorizontal: 28, paddingVertical: 26,
    borderBottomWidth: 1, borderBottomColor: Colors.accentLine,
    backgroundColor: Colors.accentSoft,
    ...(Platform.OS === 'web'
      ? { backgroundImage: 'linear-gradient(135deg, var(--accent-soft) 0%, var(--card) 70%)' } as any
      : {}),
  },
  avatarRing: {
    borderRadius: 999,
    padding: 3,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.accentLine,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 22px -6px rgba(79,70,229,0.4)' } as any
      : {}),
  },
  profileHeaderInner: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  profileHeaderInfo: { flex: 1, minWidth: 0 },
  profileName: { fontSize: 22, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.3 },
  profileMeta: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' },
  profileEmail: { fontSize: 12.5, color: Colors.ink2 },
  profilePhone: { fontSize: 12.5, color: Colors.ink2 },
  profileOrg: { fontSize: 13, color: Colors.ink3 },
  profileOrgBold: { fontWeight: FontWeight.semibold, color: Colors.ink2 },
  metaDot: { fontSize: 12, color: Colors.ink4 },
  tagsRow: { flexDirection: 'row', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  profileActions: { flexDirection: 'row', gap: 8 },

  // Body grid
  bodyGrid: {
    flexDirection: 'row', gap: 20,
    paddingHorizontal: 28, paddingTop: 20,
    flexWrap: 'wrap',
  },
  leftCol: { flex: 1, minWidth: 300, gap: 16, flexDirection: 'column' },
  rightCol: { width: 300, gap: 16, flexDirection: 'column' },

  // KPI strip
  kpiStrip: { flexDirection: 'row', gap: 12 },
  kpiCard: {
    flex: 1, padding: 14,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3,
  },
  kpiLabel: {
    fontSize: 11, fontWeight: FontWeight.semibold, color: Colors.ink4,
    textTransform: 'uppercase', letterSpacing: 0.6,
  },
  kpiValue: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, marginTop: 4, letterSpacing: -0.2 },
  kpiSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },

  // Card
  card: {
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r3, overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  cardTitle: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },

  ghostBtn: {
    paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: Radii.r2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  ghostBtnText: { fontSize: 12, color: Colors.ink3, fontWeight: FontWeight.medium },

  // Table
  tableRow: { flexDirection: 'row', alignItems: 'center' },
  tableHeader: {
    backgroundColor: Colors.paper2,
    paddingHorizontal: 12, paddingVertical: 7,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  tableDataRow: { paddingHorizontal: 12, paddingVertical: 10 },
  tableRowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  th: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { fontSize: 13, color: Colors.ink2 },
  tdMono: { fontSize: 12, color: Colors.ink3 },
  tdSub: { fontSize: 12, color: Colors.ink3 },

  emptyRow: { paddingVertical: 24, alignItems: 'center' },
  emptyRowText: { fontSize: 13, color: Colors.ink4 },

  // Timeline
  timeline: { padding: 16, gap: 12, flexDirection: 'column' },
  timelineItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  timelineIcon: {
    width: 24, height: 24, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  timelineText: { flex: 1, fontSize: 12.5, color: Colors.ink2 },
  timelineTime: { fontSize: 11.5, color: Colors.ink4, flexShrink: 0 },

  // Contact details panel
  detailsBody: { padding: 16, gap: 12, flexDirection: 'column' },
  detailSection: { gap: 6 },
  detailSectionLabel: {
    fontSize: 11, fontWeight: FontWeight.semibold, color: Colors.ink4,
    textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2,
  },
  detailRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  detailMono: { fontSize: 12.5, color: Colors.ink, flex: 1 },
  detailMonoBreak: { flexShrink: 1 },
  detailText: { fontSize: 13, color: Colors.ink },
  detailEmpty: { fontSize: 12, color: Colors.ink4, fontStyle: 'italic' },

  // Tags panel
  tagsPanel: { padding: 16, flexDirection: 'row', flexWrap: 'wrap', gap: 6 },

  // Team panel
  teamList: { padding: 16, gap: 10, flexDirection: 'column' },
  teamMember: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  teamMemberInfo: { flex: 1 },
  teamMemberName: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  teamMemberRole: { fontSize: 11.5, color: Colors.ink3 },
});
